import { Grid, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../app/hooks";
import BasicDialog from "../../../../common/components/functional/dialogs/BasicDialog";
import { TextFieldLight } from "../../../../common/components/styled/styled";
import { postSplitAssignment } from "../../../talents/talentsSlice";

interface Props {
  assignment: any;
  splitAssignmentDialogOpen: boolean;
  setSplitAssignmentDialogOpen: { (open: boolean): void };
  toSplittedAssignmentView: { (id: string): void };
}

const SplitAssignmentDialog = ({
  assignment,
  splitAssignmentDialogOpen,
  setSplitAssignmentDialogOpen,
  toSplittedAssignmentView,
}: Props) => {
  const [splitAssignmentStartDate, setSplitAssignmentStartDate] =
    useState<Date | null>(null);
  const [splitAssignmentReason, setSplitAssignmentReason] = useState<
    string | null
  >(null);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Minimum date for splitting an assignment is midnight of the day after the start date
  const splitDateMinimum = new Date(
    new Date(assignment?.details?.start).setDate(
      new Date(assignment?.details?.start).getDate() + 1,
    ),
  );

  // Set time to 00:00:00
  splitDateMinimum.setHours(0, 0, 0, 0);

  const handleSplitAssignment = async () => {
    const dateToUse = splitAssignmentStartDate || splitDateMinimum;

    if (dateToUse && splitAssignmentReason && assignment) {
      const adjustedDate = new Date(
        dateToUse.getTime() - dateToUse.getTimezoneOffset() * 60000,
      );
      const formattedSplitDate = adjustedDate.toISOString().slice(0, 10);
      const resultAction = await dispatch(
        postSplitAssignment({
          assignmentId: assignment?.assignmentId,
          version: assignment?.details?.version,
          splitDate: formattedSplitDate,
          reason: splitAssignmentReason,
        }),
      );

      // Find the event with name "assgnNew"
      const newAssignmentEvent = resultAction?.payload?.find(
        (event: any) => event?.name === "assgnNew",
      );

      if (
        resultAction?.meta?.requestStatus === "fulfilled" &&
        newAssignmentEvent
      ) {
        setSplitAssignmentReason(null);
        setSplitAssignmentStartDate(null);
        // Redirect to the new assignment view
        toSplittedAssignmentView(newAssignmentEvent.id);
      }
    }
  };

  return (
    <BasicDialog
      open={splitAssignmentDialogOpen}
      onCancel={() => setSplitAssignmentDialogOpen(false)}
      onConfirm={() => handleSplitAssignment()}
      title={t("talentProfile:splitAssignmentDialogTitle")}
      dialogContentText={t("talentProfile:splitAssignmentDialogContent")}
      confirmLabel={t("talentProfile:splitAssignment")}
      PaperProps={{ sx: { minWidth: "572px" } }}
      confirmDisabled={
        // Start date is required
        !splitAssignmentStartDate ||
        // Reason is required
        !splitAssignmentReason ||
        // Can't be before the start date
        splitAssignmentStartDate < new Date(assignment?.details?.start) ||
        // Can't be on or after the end date if provided
        (assignment?.details?.end &&
          splitAssignmentStartDate >=
            new Date(new Date(assignment.details.end))) ||
        // Can't be more than 5 years in the future
        splitAssignmentStartDate >
          new Date(new Date().setFullYear(new Date().getFullYear() + 5)) ||
        // Ensure the split assignment start date is not the same as the original start date
        splitAssignmentStartDate.getTime() ===
          new Date(assignment?.details?.start).getTime()
      }
      noBorder
      content={
        <>
          <Grid container direction="column" spacing={2} color={"#00000085"}>
            <Grid item container xs={12} alignItems="center">
              <Grid item xs={5}>
                <Typography textAlign={"end"} paddingRight={"16px"}>
                  {t("talentProfile:splitAssignmentStartDate")}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <DesktopDatePicker
                  value={null}
                  format="dd.MM.yyyy"
                  onChange={(newDate: Date | null) =>
                    setSplitAssignmentStartDate(newDate)
                  }
                  slots={{ textField: TextFieldLight }}
                  minDate={splitDateMinimum}
                  maxDate={
                    assignment?.details?.end
                      ? new Date(
                          new Date(assignment.details.end).setDate(
                            new Date(assignment.details.end).getDate() - 1,
                          ),
                        ) // One day before the end date
                      : new Date(
                          new Date().setFullYear(new Date().getFullYear() + 5),
                        ) // Default to now + 5 years
                  }
                  slotProps={{
                    textField: {
                      sx: { width: "100%" },
                      error: false,
                      size: "small",
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={5}>
                <Typography textAlign={"end"} paddingRight={"16px"}>
                  {t("talentProfile:splitAssignmentReason")}
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <TextFieldLight
                  sx={{ width: "100%" }}
                  size="small"
                  value={splitAssignmentReason || ""}
                  onChange={(e) => setSplitAssignmentReason(e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      }
    />
  );
};

export default SplitAssignmentDialog;
